

import React, {useLayoutEffect, useState} from 'react';
import {
  Backdrop,
  Modal,
  Fade,
  Typography,
  Paper,
  Button,
  Box,
  Grid,
} from '@mui/material';
import { Input } from 'components/layout';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { FormChipList, FormMonacoEditor } from 'components/common';
import { useTheme } from '@mui/material/styles';

export default function InventoryGroupModal(props) {

  const {
    open,
    handleClose,
    onSubmitCreate,
    onSubmitEdit,
    defaultValues={},
    isEdit,
    policies,
    items,
    currentHosts,
    index
  } = props;

  const [t] = useTranslation();
  const actionType = isEdit ? 'edit' : 'create';
  const methods = useForm({ defaultValues });
  const { control, handleSubmit, getValues, setError, setValue } = methods;
  const [languageSelected, setLanguageSelected] = useState('ini');
  const [hosts, setHosts] = useState([]);
  const theme = useTheme();

  useLayoutEffect(() => {
    if (currentHosts.length > 0) {
      setHosts(currentHosts.map( i => ({ value: i.ip, label: i.name })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentHosts]);

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    p: 3,
    backgroundColor: 'white',
  };

  const srollBarStyles = {
    overflowY: 'scroll',
    maxHeight:'70vh',
    '&::-webkit-scrollbar': {
      width: '16px'
    },
    '&::-webkit-scrollbar-track': {
      boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
      webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)'
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '8px',
      backgroundColor: theme.palette.primary[100],
      border: `4px solid ${theme.palette.common.white}`,
      '&:hover': {
        backgroundColor: theme.palette.primary[200]
      },
      '&:active': {
        backgroundColor: theme.palette.primary[300]
      }
    }
  }

  const fields = [
    { policy: policies.modalGroupName },
    { policy: policies.modalGroupDescription },
    { policy: policies.modalGroupHosts, options: hosts },
    { policy: policies.modalNetworkType },
  ];

  const tagsField = {...policies.modalTags, defaultValues: getValues(policies.modalTags.name)};

  function handleOnSubmit(data) {
    const newGroup = {
      name: data.modalGroupName,
      description: data.modalGroupDescription,
      tags: data.modalTags,
      hosts: data.modalGroupHosts,
      networkType: data.modalNetworkType,
      variables: data.modalVariables,
      variablesType: languageSelected,
      hostToShow: data.modalGroupHosts.map(host => host.label).join(',')
    }

    if (isEdit) {
      onSubmitEdit({...newGroup, index})
    } else {
      const hostFound = items.find(h => h.name.trim() === data.modalGroupName.trim());
      if (hostFound) {
        setError(policies.modalGroupName.name, {message: t('inventoryList.groupHosts.error.groupHostFound')});
        return;
      }
      onSubmitCreate(newGroup);
    }
  }

  const handleOnChange = (name) => {
    return (value) => setValue(name, value);
  };

  const changeLanguage = (language) => {
    setLanguageSelected(language);
    setValue('language', language);
  }

  return (
    <Modal
    aria-labelledby="transition-modal-title"
    aria-describedby="transition-modal-description"
    open={open}
    onClose={handleClose}
    closeAfterTransition
    BackdropComponent={Backdrop}
    sx={{
      backdropFilter: 'blur(2px)',
    }}
    BackdropProps={{
      timeout: 500,
    }}
  >
    <Fade in={open}>
        <Paper sx={style}>
          <FormProvider { ...methods }>
          <form>
            <Grid container spacing={2} sx={srollBarStyles}>

              <Grid item xs={12} sm={12} md={12} lg={12}>
                <Typography id="transition-modal-title" variant="h6" component="h2">
                  { t(`inventoryList.groupHosts.${actionType}.title`) }
                </Typography>
              </Grid>

              { fields.map((field, index) => (
                <Grid item key={index} xs={12} sm={12} md={6} lg={6} sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  width: '100%'
                }}>
                  <Input
                    {...field.policy}
                    control={control}
                    options={field.options}
                    setValue={methods.setValue}
                    getValues = {methods.getValues}
                    sx={{ width: '100%' }}
                  />
                </Grid>
              ))}

              <Grid item xs={12} sm={12} md={12} lg={12} key={policies.modalTags.name}>
                <FormChipList
                  {...tagsField}
                  control={control}
                  onChange={handleOnChange(tagsField.name)}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={12}>
                <FormMonacoEditor
                  label={policies.modalVariables.label}
                  name={policies.modalVariables.name}
                  helperText={policies.modalVariables.helperText}
                  methods={methods}
                  language={languageSelected}
                  handleSetLanguage={changeLanguage}
                  height='200px'
                />
              </Grid>
            </Grid>

            <Box sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              justifyContent: 'flex-end',
              gap: 2,
              mt: 2
            }}>
              <Button onClick={handleClose}>
                { t(`template.stepper.polling.modal.buttonClose`) }
              </Button>
              <Button variant='contained' onClick={handleSubmit(handleOnSubmit)}>
                { t(`template.stepper.polling.modal.${actionType}.button`) }
              </Button>
            </Box>
          </form>
        </FormProvider>
      </Paper>
    </Fade>
  </Modal>
  )
}
